import pino from 'pino';
import { err as serializeStdError } from 'pino-std-serializers';

// https://getpino.io/#/docs/help?id=mapping-pino-log-levels-to-google-cloud-logging-stackdriver-severity-levels
const PinoLevelToSeverityLookup: Record<string, string> = {
  trace: 'DEBUG',
  debug: 'DEBUG',
  info: 'INFO',
  warn: 'WARNING',
  error: 'ERROR',
  fatal: 'CRITICAL',
};

// The platform ADR "Error Log Required Context Fields" requires flat error_type and error_message.
// Node pino formats before serializing and passes the Error itself; the browser build, which the
// middleware runs, serializes first and passes `{ type, message, stack }`.
const toErrorFields = (loggedError: unknown) => {
  if (loggedError instanceof Error) {
    return { error_type: loggedError.name, error_message: loggedError.message };
  }
  if (
    typeof loggedError === 'object' &&
    loggedError !== null &&
    'type' in loggedError &&
    'message' in loggedError
  ) {
    return { error_type: loggedError.type, error_message: loggedError.message };
  }
  return undefined;
};

// pino-std-serializers instead of `pino.stdSerializers`: the browser build ships its own `err`
// serializer, which drops `cause` and logs a nested Error such as `networkError` as `{}`.
// The serializer treats any object with a string `message` as an Error, hence the guard.
const serializeError = (value: unknown) =>
  value instanceof Error ? serializeStdError(value) : value;

export const sharedOptions: Parameters<typeof pino>[0] = {
  messageKey: 'message',
  // `err` is implicit in Node but must be named for the browser build's `serialize: true`;
  // `error` is named because neither build serializes it, so a `{ error }` call site logs `{}`.
  serializers: { err: serializeError, error: serializeError },
  formatters: {
    level(label, number) {
      return {
        severity: PinoLevelToSeverityLookup[label] || PinoLevelToSeverityLookup.info,
        level: number,
      };
    },
    log(logged) {
      const object: Record<string, unknown> = {
        ...logged,
        ...toErrorFields(logged.err ?? logged.error),
      };

      // transform errors to conform to https://cloud.google.com/error-reporting/reference/rest/v1beta1/projects.events/report#ReportedErrorEvent
      if (object.stack) {
        const {
          time,
          message: originalMessage,
          stack: message,
          msg: errorMessage,
          type: errorType,
          ...rest
        } = object;

        return {
          ...rest,
          time,
          ...(typeof time === 'number' && { eventTime: new Date(time).toISOString() }),
          message,
          errorMessage,
          errorType,
          originalMessage,
          // TODO fill service context to conform to https://cloud.google.com/error-reporting/reference/rest/v1beta1/projects.events/report#ReportedErrorEvent
          // serviceContext: {
          //   service: '', // get service name from somewhere
          //   version: '', // get commit hash maybe
          // }
        };
      }

      return object;
    },
  },
};

export const serverOnlyLogger = pino({
  ...sharedOptions,
  browser: { disabled: true },
});

export const middlewareLogger = pino({
  ...sharedOptions,
  browser: {
    // eslint-disable-next-line no-console
    write: (o) => console.log(JSON.stringify(o)),
    formatters: sharedOptions.formatters, // regular formatters are apparently ignored for `browser` (which the nextjs middleware runs as)
    serialize: true, // as the middleware is not really a `browser`, we just handle it like it isn't
  },
}).child({ location: 'middleware' });

export const logger = pino({
  ...sharedOptions,
});
